// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how Home is laid out on this device, all tunable in Settings > Customise Home
// (design/home-mobile-compact round 2): the layout, density, which sections show and in
// what order, how many rows a section shows before "+N more", whether Konvo's suggestions
// stack or list, what swiping a Home row does each way, how a conversation opens from Home
// (the pop-up, or side by side with Home as a list), whether the source rail shows and how
// All chats is grouped (by day, or by connection).

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {makeAutoObservable} from 'mobx';

/**
 * Stacks = design A (calm, stacked sections); List = design B (every item a swipe row);
 * Deck = design C (the to-dos one at a time in a card at the top); Timeline = design 5a (the
 * to-dos slotted into the free time between today's meetings).
 */
export type HomeLayout = 'stacks' | 'list' | 'deck' | 'timeline';
export type HomeDensity = 'comfortable' | 'compact';
export type HomeSectionId = 'today' | 'konvo' | 'dashboards' | 'sorted' | 'all_chats' | 'coming_up';
export type HomeSuggestionStyle = 'stack' | 'list';
export type HomeSwipeAction = 'done' | 'reply' | 'later' | 'dismiss' | 'task' | 'ask' | 'none';
export type HomeSwipeDirection = 'left' | 'right';
/** A short swipe (past the first line) or a long one (past the second). */
export type HomeSwipeReach = 'short' | 'long';
export type HomeSwipeDistance = 'short' | 'medium' | 'long';
/** Pop-up: a card over Home (Expand opens it beside). Side: Home moves into a list and the chat opens beside it. */
export type HomeOpenConversations = 'popup' | 'side';
export const HOME_OPEN_CONVERSATIONS: ReadonlyArray<HomeOpenConversations> = ['popup', 'side'];
/** All chats under Today, Yesterday, the weekdays and Earlier, or one group per connection. Newest first either way. */
export type HomeAllChatsGrouping = 'day' | 'connection';
export const HOME_ALL_CHATS_GROUPINGS: ReadonlyArray<HomeAllChatsGrouping> = ['day', 'connection'];

export interface HomeSectionSetting {
	id: HomeSectionId;
	visible: boolean;
}

export const HOME_LAYOUTS: ReadonlyArray<HomeLayout> = ['stacks', 'list', 'deck', 'timeline'];
export const HOME_SECTION_IDS: ReadonlyArray<HomeSectionId> = [
	'today',
	'konvo',
	'dashboards',
	'sorted',
	'all_chats',
	'coming_up',
];
export const HOME_SWIPE_ACTIONS: ReadonlyArray<HomeSwipeAction> = [
	'done',
	'reply',
	'later',
	'dismiss',
	'task',
	'ask',
	'none',
];
/** Rows a section shows before "+N more"; 0 means all of them. */
export const HOME_ROW_CHOICES: ReadonlyArray<number> = [3, 5, 7, 0];
/** 0 disables undo send; replies go out immediately. */
export const HOME_UNDO_SEND_CHOICES: ReadonlyArray<number> = [0, 3, 5, 7];
export const HOME_UNDO_SEND_DEFAULT_SECONDS = 3;

/** Share of the row width the first line (short swipe) sits at. The second line is fixed. */
export const HOME_SWIPE_DISTANCE_RATIO: Record<HomeSwipeDistance, number> = {short: 0.22, medium: 0.3, long: 0.4};
export const HOME_SWIPE_LONG_RATIO = 0.65;

/** Off until switched on in Customise Home. Sorted for you went off by default on 2026-10-06. */
const HIDDEN_BY_DEFAULT: ReadonlyArray<HomeSectionId> = ['sorted'];

const DEFAULT_SECTIONS: ReadonlyArray<HomeSectionSetting> = HOME_SECTION_IDS.map((id) => ({
	id,
	visible: !HIDDEN_BY_DEFAULT.includes(id),
}));

/**
 * Every known section exactly once, in the stored order. A section added since the order was
 * saved goes in after the section it follows by default (at the top when it leads), shown or
 * hidden as it is by default.
 */
export function normalizeSections(value: unknown): Array<HomeSectionSetting> {
	const out: Array<HomeSectionSetting> = [];
	if (Array.isArray(value)) {
		for (const entry of value) {
			const id = (entry as HomeSectionSetting | null)?.id;
			if (!HOME_SECTION_IDS.includes(id as HomeSectionId) || out.some((section) => section.id === id)) continue;
			out.push({id: id as HomeSectionId, visible: (entry as HomeSectionSetting).visible !== false});
		}
	}
	HOME_SECTION_IDS.forEach((id, index) => {
		if (out.some((section) => section.id === id)) return;
		const before = index > 0 ? out.findIndex((section) => section.id === HOME_SECTION_IDS[index - 1]) : -1;
		out.splice(before + 1, 0, {id, visible: !HIDDEN_BY_DEFAULT.includes(id)});
	});
	return out;
}

export function normalizeSwipeAction(value: unknown, fallback: HomeSwipeAction): HomeSwipeAction {
	return HOME_SWIPE_ACTIONS.includes(value as HomeSwipeAction) ? (value as HomeSwipeAction) : fallback;
}

const SWIPE_DEFAULTS: Record<HomeSwipeDirection, Record<HomeSwipeReach, HomeSwipeAction>> = {
	right: {short: 'done', long: 'reply'},
	left: {short: 'later', long: 'dismiss'},
};

class HomePreferences {
	layout: HomeLayout = 'stacks';
	density: HomeDensity = 'comfortable';
	sections: Array<HomeSectionSetting> = DEFAULT_SECTIONS.map((section) => ({...section}));
	rowsBeforeMore = 3;
	suggestionStyle: HomeSuggestionStyle = 'stack';
	/** Open the focus deck straight away when Today has at least this many things (0 = never). Not in the Deck layout. */
	deckThreshold = 0;
	undoSendSeconds = HOME_UNDO_SEND_DEFAULT_SECONDS;
	rightShort: HomeSwipeAction = SWIPE_DEFAULTS.right.short;
	rightLong: HomeSwipeAction = SWIPE_DEFAULTS.right.long;
	leftShort: HomeSwipeAction = SWIPE_DEFAULTS.left.short;
	leftLong: HomeSwipeAction = SWIPE_DEFAULTS.left.long;
	/** A short swipe that stops before the first line rests open on a tray of the left actions. */
	trayEnabled = true;
	distance: HomeSwipeDistance = 'medium';
	haptics = true;
	openConversations: HomeOpenConversations = 'popup';
	/** The logos on Home's right edge that show one source (desktop). */
	sourceRail = true;
	/** Sections folded down to their heading (the chevron beside it). */
	collapsed: Array<HomeSectionId> = [];
	allChatsGrouping: HomeAllChatsGrouping = 'day';
	/** Connections folded down to their heading in All chats grouped by connection ('konvo', 'guild:<id>', ...). */
	collapsedChatGroups: Array<string> = [];
	/** Sorted for you was switched off once for orders saved while it was on by default. */
	sortedOffOnce = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void Promise.resolve(
			makePersistent(
				this,
				'HomePreferences',
				[
					'layout',
					'density',
					'sections',
					'rowsBeforeMore',
					'suggestionStyle',
					'deckThreshold',
					'undoSendSeconds',
					'rightShort',
					'rightLong',
					'leftShort',
					'leftLong',
					'trayEnabled',
					'distance',
					'haptics',
					'openConversations',
					'sourceRail',
					'collapsed',
					'allChatsGrouping',
					'collapsedChatGroups',
					'sortedOffOnce',
				],
				{syncAcrossTabs: true},
			),
		).then(this.switchSortedOffOnce);
	}

	/** Saved section orders kept Sorted for you on; switch it off once, after which the switch is theirs. */
	switchSortedOffOnce(): void {
		if (this.sortedOffOnce) return;
		this.setSectionVisible('sorted', false);
		this.sortedOffOnce = true;
	}

	get effectiveLayout(): HomeLayout {
		return HOME_LAYOUTS.includes(this.layout) ? this.layout : 'stacks';
	}

	/** Sections in display order, hidden ones included (the settings list needs them). */
	get orderedSections(): Array<HomeSectionSetting> {
		return normalizeSections(this.sections);
	}

	/** Visible sections in display order. */
	get visibleSections(): Array<HomeSectionId> {
		return this.orderedSections.filter((section) => section.visible).map((section) => section.id);
	}

	/** Rows before "+N more"; Infinity when the choice is "all". */
	get rowLimit(): number {
		const rows = HOME_ROW_CHOICES.includes(this.rowsBeforeMore) ? this.rowsBeforeMore : 3;
		return rows === 0 ? Number.POSITIVE_INFINITY : rows;
	}

	get distanceRatio(): number {
		return HOME_SWIPE_DISTANCE_RATIO[this.distance] ?? HOME_SWIPE_DISTANCE_RATIO.medium;
	}

	get undoSendMs(): number {
		return (
			(HOME_UNDO_SEND_CHOICES.includes(this.undoSendSeconds) ? this.undoSendSeconds : HOME_UNDO_SEND_DEFAULT_SECONDS) *
			1000
		);
	}

	swipeAction(direction: HomeSwipeDirection, reach: HomeSwipeReach): HomeSwipeAction {
		const stored =
			direction === 'right'
				? reach === 'short'
					? this.rightShort
					: this.rightLong
				: reach === 'short'
					? this.leftShort
					: this.leftLong;
		return normalizeSwipeAction(stored, SWIPE_DEFAULTS[direction][reach]);
	}

	setLayout(layout: HomeLayout): void {
		this.layout = HOME_LAYOUTS.includes(layout) ? layout : 'stacks';
	}

	setDensity(density: HomeDensity): void {
		this.density = density === 'compact' ? 'compact' : 'comfortable';
	}

	setSectionVisible(id: HomeSectionId, visible: boolean): void {
		this.sections = this.orderedSections.map((section) => (section.id === id ? {...section, visible} : section));
	}

	/** Moves a section to `index` in the order. */
	moveSection(id: HomeSectionId, index: number): void {
		const sections = this.orderedSections;
		const from = sections.findIndex((section) => section.id === id);
		if (from < 0) return;
		const [moved] = sections.splice(from, 1);
		sections.splice(Math.max(0, Math.min(index, sections.length)), 0, moved);
		this.sections = sections;
	}

	setRowsBeforeMore(rows: number): void {
		this.rowsBeforeMore = HOME_ROW_CHOICES.includes(rows) ? rows : 3;
	}

	setSuggestionStyle(style: HomeSuggestionStyle): void {
		this.suggestionStyle = style === 'list' ? 'list' : 'stack';
	}

	setDeckThreshold(count: number): void {
		this.deckThreshold = Number.isFinite(count) && count > 0 ? Math.round(count) : 0;
	}

	setUndoSendSeconds(seconds: number): void {
		this.undoSendSeconds = HOME_UNDO_SEND_CHOICES.includes(seconds) ? seconds : HOME_UNDO_SEND_DEFAULT_SECONDS;
	}

	setSwipeAction(direction: HomeSwipeDirection, reach: HomeSwipeReach, action: HomeSwipeAction): void {
		const value = normalizeSwipeAction(action, SWIPE_DEFAULTS[direction][reach]);
		if (direction === 'right') {
			if (reach === 'short') this.rightShort = value;
			else this.rightLong = value;
		} else if (reach === 'short') this.leftShort = value;
		else this.leftLong = value;
	}

	setTrayEnabled(enabled: boolean): void {
		this.trayEnabled = enabled;
	}

	setDistance(distance: HomeSwipeDistance): void {
		this.distance = distance in HOME_SWIPE_DISTANCE_RATIO ? distance : 'medium';
	}

	setHaptics(enabled: boolean): void {
		this.haptics = enabled;
	}

	get effectiveOpenConversations(): HomeOpenConversations {
		return this.openConversations === 'side' ? 'side' : 'popup';
	}

	setOpenConversations(value: HomeOpenConversations): void {
		this.openConversations = value === 'side' ? 'side' : 'popup';
	}

	setSourceRail(enabled: boolean): void {
		this.sourceRail = enabled;
	}

	isCollapsed(id: HomeSectionId): boolean {
		return Array.isArray(this.collapsed) && this.collapsed.includes(id);
	}

	toggleCollapsed(id: HomeSectionId): void {
		const current = Array.isArray(this.collapsed) ? this.collapsed : [];
		this.collapsed = current.includes(id)
			? current.filter((entry) => entry !== id)
			: [...current.filter((entry) => HOME_SECTION_IDS.includes(entry)), id];
	}

	get effectiveAllChatsGrouping(): HomeAllChatsGrouping {
		return this.allChatsGrouping === 'connection' ? 'connection' : 'day';
	}

	setAllChatsGrouping(value: HomeAllChatsGrouping): void {
		this.allChatsGrouping = value === 'connection' ? 'connection' : 'day';
	}

	isChatGroupCollapsed(source: string): boolean {
		return Array.isArray(this.collapsedChatGroups) && this.collapsedChatGroups.includes(source);
	}

	toggleChatGroupCollapsed(source: string): void {
		const current = Array.isArray(this.collapsedChatGroups) ? this.collapsedChatGroups : [];
		this.collapsedChatGroups = current.includes(source)
			? current.filter((entry) => entry !== source)
			: [...current, source];
	}

	reset(): void {
		this.layout = 'stacks';
		this.density = 'comfortable';
		this.sections = DEFAULT_SECTIONS.map((section) => ({...section}));
		this.rowsBeforeMore = 3;
		this.suggestionStyle = 'stack';
		this.deckThreshold = 0;
		this.undoSendSeconds = HOME_UNDO_SEND_DEFAULT_SECONDS;
		this.rightShort = SWIPE_DEFAULTS.right.short;
		this.rightLong = SWIPE_DEFAULTS.right.long;
		this.leftShort = SWIPE_DEFAULTS.left.short;
		this.leftLong = SWIPE_DEFAULTS.left.long;
		this.trayEnabled = true;
		this.distance = 'medium';
		this.haptics = true;
		this.openConversations = 'popup';
		this.sourceRail = true;
		this.collapsed = [];
		this.allChatsGrouping = 'day';
		this.collapsedChatGroups = [];
	}
}

export default new HomePreferences();
